/**
 * Services Section - "What We Build"
 * ===================================
 * 
 * OPTIMIZED VERSION:
 * - No typewriting animation
 * - No scroll-triggered loading - loads immediately
 * - Original layout preserved
 */

import React, { useState, useRef, useCallback, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { services, categories, getServicesByCategory } from '../../data/services';
import ServicesParticles from './ServicesParticles';
import './Services.css';

// ============================================
// OPTIMIZED 3D CARD COMPONENT - LOADS IMMEDIATELY
// ============================================
const ServiceCard = memo(({ service, index, onClick }) => {
  const cardRef = useRef(null);
  const [transform, setTransform] = useState({ rotateX: 0, rotateY: 0 });

  // Mouse handlers for 3D effect
  const handleMouseMove = useCallback((e) => {
    const card = cardRef.current;
    if (!card) return;

    const rect = card.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;

    setTransform({
      rotateX: y * -20,
      rotateY: x * 20
    });
  }, []);

  const handleMouseLeave = useCallback(() => {
    setTransform({ rotateX: 0, rotateY: 0 });
  }, []);

  return (
    <div
      ref={cardRef}
      className={`service-card service-${service.color} in-view`}
      style={{
        transform: `perspective(1200px) rotateX(${transform.rotateX}deg) rotateY(${transform.rotateY}deg)`,
        opacity: 1,
        transition: 'transform 0.15s ease'
      }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onClick={() => onClick(service)}
    >
      <div className="card-inner">
        <div className="service-header">
          <div className="service-icon">
            <i className={`fas ${service.icon}`} />
          </div>
          <div className="service-badge">
            <i className={`fas ${service.badgeIcon}`} />
            <span>{service.badge}</span>
          </div>
        </div>

        <div className="service-content">
          <span className="service-category">{service.category}</span>
          <h3>{service.title}</h3>
          <p className="service-tagline">{service.tagline}</p>
          <p className="service-desc">{service.description}</p>

          <div className="service-stats">
            {service.stats.slice(0, 3).map((stat, j) => (
              <div key={j} className="s-stat">
                <span className="s-val">{stat.value}</span>
                <span className="s-label">{stat.label}</span>
              </div>
            ))}
          </div>

          <div className="service-tech">
            {service.tech.slice(0, 3).map((t, j) => (
              <span key={j} className="tech-pill">{t}</span>
            ))}
            {service.tech.length > 3 && (
              <span className="tech-more">+{service.tech.length - 3}</span>
            )}
          </div>

          <div className="service-footer">
            <span>View Details</span>
            <i className="fas fa-arrow-right" />
          </div>
        </div>
      </div>

      <div className="service-glow" />
      <div className="card-corner card-corner-tl" />
      <div className="card-corner card-corner-br" />
    </div>
  );
});

ServiceCard.displayName = 'ServiceCard';

// ============================================
// SERVICE MODAL - NO TYPEWRITING
// ============================================
const ServiceModal = ({ service, onClose }) => {
  const modalRef = useRef(null);

  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <motion.div
      className="service-modal-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      onClick={onClose}
    >
      <motion.div
        ref={modalRef}
        className={`service-modal modal-${service.color}`}
        initial={{ opacity: 0, y: 50, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 30, scale: 0.95 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button 
          className="modal-close" 
          onClick={onClose}
          aria-label="Close modal"
        >
          <i className="fas fa-times" />
        </button>

        {/* Modal Header */}
        <div className="modal-header">
          <div className="modal-image">
            <img 
              src={service.image} 
              alt={service.title}
              onError={(e) => {
                e.target.style.display = 'none';
                if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div className="image-fallback" style={{ display: 'none' }}>
              <i className={`fas ${service.icon}`} />
              <span>Image Coming Soon</span>
            </div>
          </div>

          <div className="modal-header-content">
            <div className="modal-icon">
              <i className={`fas ${service.icon}`} />
            </div>
            <div className="modal-title">
              <span className="modal-badge">{service.badge}</span>
              <h2>{service.title}</h2>
              <p className="modal-tagline">{service.tagline}</p>
            </div>
          </div>
        </div>

        {/* Modal Body */}
        <div className="modal-body animate">
          {/* Overview */}
          <div className="modal-section">
            <h4>
              <i className="fas fa-info-circle" />
              Overview
            </h4>
            <p>{service.longDescription}</p>
          </div>

          {/* Features */}
          <div className="modal-section">
            <h4>
              <i className="fas fa-star" />
              Key Features
            </h4>
            <div className="modal-features">
              {service.features.map((f, i) => (
                <div 
                  key={i} 
                  className="modal-feature"
                  style={{ animationDelay: `${i * 0.1}s` }}
                >
                  <div className="mf-icon">
                    <i className={`fas ${f.icon}`} />
                  </div>
                  <div className="mf-content">
                    <h5>{f.title}</h5>
                    <p>{f.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Stats */}
          <div className="modal-section">
            <h4>
              <i className="fas fa-chart-bar" />
              Performance Metrics
            </h4>
            <div className="modal-stats">
              {service.stats.map((s, i) => (
                <div 
                  key={i} 
                  className="ms-item"
                  style={{ animationDelay: `${0.3 + i * 0.1}s` }}
                >
                  <span className="ms-value">{s.value}</span>
                  <span className="ms-label">{s.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Tech Stack */}
          <div className="modal-section">
            <h4>
              <i className="fas fa-cogs" />
              Technology Stack
            </h4>
            <div className="modal-tech">
              {service.tech.map((t, i) => (
                <span 
                  key={i} 
                  className="mt-tag"
                  style={{ animationDelay: `${0.5 + i * 0.05}s` }}
                >
                  {t}
                </span>
              ))}
            </div>
          </div>

          {/* Process */}
          <div className="modal-section">
            <h4>
              <i className="fas fa-tasks" />
              Our Process
            </h4>
            <div className="modal-process">
              {service.process.map((p, i) => (
                <div 
                  key={i} 
                  className="mp-step"
                  style={{ animationDelay: `${0.6 + i * 0.1}s` }}
                >
                  <span className="mp-num">{String(i + 1).padStart(2, '0')}</span>
                  <span className="mp-text">{p}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="modal-footer">
          <a
            href="https://wa.me/919962461250"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-cta"
          >
            <i className="fab fa-whatsapp" />
            <span>Get Started</span>
          </a>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ============================================
// MAIN SERVICES COMPONENT - NO SCROLL TRIGGERS
// ============================================
const Services = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedService, setSelectedService] = useState(null);

  const filteredServices = getServicesByCategory(activeCategory);

  // Close modal handler
  const handleCloseModal = useCallback(() => {
    setSelectedService(null);
  }, []);

  return (
    <section id="services" className="services-section">
      {/* Particles Background */}
      <div className="services-bg">
        <ServicesParticles />
        <div className="services-grid-bg" />
        <div className="services-vignette" />
      </div>

      <div className="services-container">
        {/* Section Header - Always visible */}
        <div className="services-header in-view">
          <div className="section-badge">
            <span className="badge-dot" />
            <span>Our Services</span>
          </div>
          <h2 className="section-title">
            What We <span className="text-highlight">Build</span>
          </h2>
          <p className="section-subtitle">
            End-to-end blockchain and software solutions for the decentralized era
          </p>
        </div>

        {/* Category Filters - Always visible */}
        <div className="services-filters in-view">
          {categories.map((cat, i) => (
            <button
              key={cat.id}
              className={`filter-btn ${activeCategory === cat.id ? 'active' : ''}`}
              onClick={() => setActiveCategory(cat.id)}
            >
              <i className={`fas ${cat.icon}`} />
              <span>{cat.label}</span>
              {cat.count && <span className="filter-count">{cat.count}</span>}
            </button>
          ))}
        </div>

        {/* Services Grid - All cards visible immediately */}
        <div className="services-grid">
          {filteredServices.map((service, i) => (
            <ServiceCard
              key={service.id}
              service={service}
              index={i}
              onClick={setSelectedService}
            />
          ))}
        </div>
      </div>

      {/* Service Detail Modal */}
      <AnimatePresence>
        {selectedService && (
          <ServiceModal
            key="modal"
            service={selectedService}
            onClose={handleCloseModal}
          />
        )}
      </AnimatePresence>
    </section>
  );
};

export default Services;
